import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { ChatMessage, ChatSession, EmptyChatSuggestedQuestion } from 'src/interfaces/Chat';

interface chatStore {
  loading: boolean;
  sessionsLoading: boolean;
  questionMode: boolean;
  stopStream: boolean;
  selectedSession: ChatSession;
  messages: ChatMessage[];
  sessions: ChatSession[];
  tracelessMessages: ChatMessage[];
  traceless: boolean;
  streaming: boolean;
  suggestedQuestions: string[];
  activeSubject: string;
  subjects: string[];
  subjectsLoading: boolean;
  isCreatingNewSession: boolean;
  emptyChatSuggestedQuestions: EmptyChatSuggestedQuestion[];
}

const initialState: chatStore = {
  loading: false,
  stopStream: false,
  questionMode: false,
  sessionsLoading: false,
  selectedSession: { id: '', title: '', created_at: '', updated_at: '' },
  sessions: [],
  messages: [],
  tracelessMessages: [],
  streaming: false,
  subjects: [],
  subjectsLoading: false,
  traceless: false,
  suggestedQuestions: [],
  activeSubject: '',
  isCreatingNewSession: false,
  emptyChatSuggestedQuestions: [],
};

const chat = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveSubject: (state, action: PayloadAction<string>) => {
      state.activeSubject = action.payload;
    },
    setSubjects: (state, action: PayloadAction<string[]>) => {
      state.subjects = action.payload;
    },
    setSubjectsLoading: (state, action: PayloadAction<boolean>) => {
      state.subjectsLoading = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSessionsLoading: (state, action: PayloadAction<boolean>) => {
      state.sessionsLoading = action.payload;
    },
    setIsCreatingNewSession: (state, action: PayloadAction<boolean>) => {
      state.isCreatingNewSession = action.payload;
    },
    setQuestionMode: (state, action: PayloadAction<boolean>) => {
      state.questionMode = action.payload;
    },
    setTraceless: (state, action: PayloadAction<boolean>) => {
      state.traceless = action.payload;
    },
    setStreaming: (state, action: PayloadAction<boolean>) => {
      state.streaming = action.payload;
    },
    setStopStream: (state, action: PayloadAction<boolean>) => {
      state.stopStream = action.payload;
    },
    setSelectedSession: (
      state,
      action: PayloadAction<ChatSession | undefined>
    ) => {
      state.selectedSession = action.payload || {
        id: '',
        title: '',
        created_at: '',
        updated_at: '',
      };
    },
    removeLastMessages: (state, action: PayloadAction<number>) => {
      state.messages = state.messages.slice(0, -action.payload);
    },
    addSessions: (state, action: PayloadAction<ChatSession>) => {
      state.sessions = [action.payload, ...state.sessions];
    },
    setSessions: (state, action: PayloadAction<ChatSession[]>) => {
      state.sessions = action.payload;
    },
    setSessionTitle: (
      state,
      action: PayloadAction<{ title: string; id: string }>
    ) => {
      let tempSessions = state.sessions;
      const session_index = tempSessions.findIndex(
        (val) => val.id == action.payload.id
      );
      tempSessions[session_index].title = action.payload.title;

      state.sessions = tempSessions;
    },
    removeSession: (state, action: PayloadAction<string>) => {
      let sessions = state.sessions;
      let deleteIndex = sessions.findIndex((item) => item.id == action.payload);

      if (deleteIndex > -1) {
        sessions.splice(deleteIndex, 1);
      }
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    streamChatMessage: (state, action: PayloadAction<string>) => {
      const chunk = action.payload;
      const lastMessageIndex = state.messages.length - 1;
      const lastMessage = state.messages[lastMessageIndex];

      // Append the chunk to the last message
      state.messages[lastMessageIndex].content =
        lastMessage.content.concat(chunk);
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages = [...state.messages, action.payload];
    },
    clearMessages: (state) => {
      state.messages = state.messages.slice(0, 1);
    },
    addTracelessMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.tracelessMessages = [...state.tracelessMessages, action.payload];
    },
    streamTracelessChatMessage: (state, action: PayloadAction<string>) => {
      const chunk = action.payload;
      const lastMessageIndex = 1;
      const lastMessage = state.tracelessMessages[lastMessageIndex];

      // Append the chunk to the last message
      state.tracelessMessages[lastMessageIndex].content =
        lastMessage.content.concat(chunk);
    },
    clearTracelessMessages: (state) => {
      state.tracelessMessages = [];
    },
    setSuggestedQuestions: (state, action: PayloadAction<string[]>) => {
      state.suggestedQuestions = action.payload;
    },
    clearSuggestedQuestions: (state) => {
      state.suggestedQuestions = [];
    },
    clearChatState: (state) => {
      state.sessions = [];
      state.selectedSession = {
        id: '',
        title: '',
        created_at: '',
        updated_at: '',
      };
      state.messages = [];
    },
    setEmptyChatSuggestedQuestions: (state, action: PayloadAction<EmptyChatSuggestedQuestion[]>) => {
      state.emptyChatSuggestedQuestions = action.payload;
    },
  },
});

export const {
  setActiveSubject,
  setSubjects,
  setSubjectsLoading,
  setLoading,
  setMessages,
  setSelectedSession,
  setSessions,
  setSessionsLoading,
  setQuestionMode,
  setStreaming,
  addSessions,
  removeSession,
  setSessionTitle,
  clearChatState,
  setTraceless,
  streamChatMessage,
  addMessage,
  clearMessages,
  addTracelessMessage,
  streamTracelessChatMessage,
  clearTracelessMessages,
  setStopStream,
  setSuggestedQuestions,
  clearSuggestedQuestions,
  removeLastMessages,
  setIsCreatingNewSession,
  setEmptyChatSuggestedQuestions,
} = chat.actions;

export const updateChatLoading = (loading: boolean) => ({
  type: setLoading.type,
  payload: loading,
});

export const stopStream = (stream: boolean) => ({
  type: setStopStream.type,
  payload: stream,
});

export const updateChatList = (chats: ChatMessage[]) => ({
  type: setMessages.type,
  payload: chats,
});

export const updateStreaming = (streaming: boolean) => ({
  type: setStreaming.type,
  payload: streaming,
});

export const streamMessage = (message: string) => ({
  type: streamChatMessage.type,
  payload: message,
});

export const appendMessageList = (chat: ChatMessage) => ({
  type: addMessage.type,
  payload: chat,
});

export const appendTracelessMessageList = (chat: ChatMessage) => ({
  type: addTracelessMessage.type,
  payload: chat,
});

export const updateSuggestedQuestionsList = (suggestedQuestions: string[]) => ({
  type: setSuggestedQuestions.type,
  payload: suggestedQuestions,
});


export default chat.reducer;
